import { useCallback, useEffect, useRef, useState } from "react";
import { useBeforeUnload, useNavigate } from "react-router-dom";

const NAV_LINK_ATTRIBUTE = "data-acx-nav-link";

/** Spread onto a control that navigates imperatively (no real `<a>`) so the guard can catch it. */
export const navLinkProps = { [NAV_LINK_ATTRIBUTE]: "true" } as const;

type PendingNavigation =
    | { type: "path"; path: string }
    | { type: "replay"; target: HTMLElement };

interface UseUnsavedChangesNavigationGuardOptions {
    hasUnsavedChanges: () => boolean;
    /** Perform the save, then call `onSaved` to resume the navigation that triggered it. */
    triggerSave: (onSaved: () => void) => void;
}

/**
 * Blocks in-app navigation while `hasUnsavedChanges()` is true, until the caller's dialog
 * resolves it via the returned handlers. Catches `<a>` clicks directly; for a control marked
 * with `NAV_LINK_ATTRIBUTE` (no real `<a>`, e.g. a tab that calls `navigate()` itself), it
 * replays the click after confirmation so that control's own logic runs. A refresh or tab close
 * falls back to the browser's native prompt, the only warning available at that point.
 */
export function useUnsavedChangesNavigationGuard({
    hasUnsavedChanges,
    triggerSave,
}: UseUnsavedChangesNavigationGuardOptions) {
    const navigate = useNavigate();
    const [pending, setPending] = useState<PendingNavigation>();
    // Set right before replaying a click so the listener below ignores that one.
    const bypassNextClick = useRef(false);

    useEffect(() => {
        function onClick(event: MouseEvent) {
            if (bypassNextClick.current) {
                bypassNextClick.current = false;
                return;
            }
            if (
                event.defaultPrevented ||
                event.button !== 0 ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey
            )
                return;
            if (!hasUnsavedChanges()) return;

            // stopPropagation, not just preventDefault, because a control's own click handler
            // (e.g. a MUI Tab's onClick) runs regardless of preventDefault — only an <a>'s
            // navigation is a "default action" that preventDefault alone can cancel.
            const intercept = (next: PendingNavigation) => {
                event.preventDefault();
                event.stopPropagation();
                setPending(next);
            };

            const target = event.target as HTMLElement;
            const anchor = target?.closest("a");
            if (anchor) {
                // Opens elsewhere or leaves the app entirely; the current tab's state isn't at risk.
                if (anchor.target && anchor.target !== "_self") return;
                if (anchor.origin !== window.location.origin) return;
                // Downloads (e.g. a blob: CSV export) and non-page links like mailto: don't navigate.
                if (
                    anchor.hasAttribute("download") ||
                    anchor.protocol !== window.location.protocol
                )
                    return;

                intercept({
                    type: "path",
                    path: anchor.pathname + anchor.search + anchor.hash,
                });
                return;
            }

            const navControl = target?.closest<HTMLElement>(
                `[${NAV_LINK_ATTRIBUTE}]`,
            );
            if (!navControl) return;

            intercept({ type: "replay", target: navControl });
        }

        // Capture phase: a link's own handler can stop propagation before it bubbles here.
        document.addEventListener("click", onClick, true);
        return () => document.removeEventListener("click", onClick, true);
    }, [hasUnsavedChanges]);

    useBeforeUnload(
        useCallback(
            (event: BeforeUnloadEvent) => {
                if (hasUnsavedChanges()) event.preventDefault();
            },
            [hasUnsavedChanges],
        ),
    );

    const cancel = () => setPending(undefined);
    const proceed = () => {
        if (pending?.type === "path") {
            navigate(pending.path);
        } else if (pending?.type === "replay") {
            bypassNextClick.current = true;
            pending.target.dispatchEvent(
                new MouseEvent("click", { bubbles: true, cancelable: true }),
            );
        }
        cancel();
    };

    return {
        isBlocked: !!pending,
        onCancel: cancel,
        onDiscard: proceed,
        onSaveFirst: () => {
            // Leave only the save dialog open, rather than stacking it on top of this one.
            cancel();
            triggerSave(proceed);
        },
    };
}
